import { type FC, type MouseEventHandler } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'
import styles from './button.module.css'

export const BUTTON_STYLED_BASE_CLASS =
  'border rounded-lg whitespace-nowrap focus-visible:outline-transparent text-center flex items-center justify-center'

export const BUTTON_STYLED_PADDING_CLASS_MAP: Record<ButtonSize, string> = {
  small: 'px-sm py-2xs',
  default: 'px-md py-xs',
}
export const BUTTON_STYLED_TEXT_SIZE_CLASS_MAP: Record<ButtonSize, string> = {
  small: 'typo-primary-sm-semibold',
  default: 'typo-primary-base-semibold',
}

export enum ButtonVariant {
  Primary = 'primary',
  Secondary = 'secondary',
  Danger = 'danger',
  Text = 'text',
}

export type ButtonSize = 'small' | 'default'

export interface ButtonProps extends DefaultProps {
  /**
   * Click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /**
   * Size
   */
  size?: ButtonSize
  /**
   * Button Type
   */
  variant?: ButtonVariant | 'primary' | 'secondary' | 'danger' | 'text'
  /**
   * Disable Button from being clicked
   */
  isDisabled?: boolean
  /**
   * Loading state of the button, disables from being clicked
   */
  isLoading?: boolean
  /**
   * Loading Content to show when isLoading is true
   */
  loadingContent?: string
  /**
   * Button type
   */
  type?: 'button' | 'submit'
}

export const Button: FC<ButtonProps> = ({
  onClick,
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  isLoading = false,
  isDisabled = false,
  loadingContent,
  children,
  variant = ButtonVariant.Primary,
  size = 'default',
  type = 'button',
}) => {
  const showHoverActive = !isDisabled && !isLoading

  const BUTTON_PRIMARY_TEXT_CLASS = cx('text-btnPrimaryText disabled:text-btnPrimaryTextDisabled', {
    'active:text-btnPrimaryTextActive hover:text-btnPrimaryTextHover': showHoverActive,
  })

  const BUTTON_PRIMARY_BG_CLASS = cx('bg-btnPrimaryBg disabled:bg-btnPrimaryBgDisabled', {
    'active:bg-btnPrimaryBgActive hover:bg-btnPrimaryBgHover': showHoverActive,
  })

  const BUTTON_SECONDARY_BG_CLASS = cx('bg-btnSecondaryBg disabled:bg-btnSecondaryBgDisabled', {
    'active:bg-btnSecondaryBgActive hover:bg-btnSecondaryBgHover': showHoverActive,
  })

  const BUTTON_DANGER_BG_CLASS = cx('bg-btnDestructionBg disabled:bg-btnDestructionBgDisabled', {
    'active:bg-btnDestructionBgActive hover:bg-btnDestructionBgHover': showHoverActive,
  })

  const BUTTON_SECONDARY_TEXT_CLASS = cx('text-btnSecondaryText disabled:text-btnSecondaryTextDisabled', {
    'active:text-btnSecondaryTextActive hover:text-btnSecondaryTextHover': showHoverActive,
  })

  const BUTTON_SECONDARY_BORDER_CLASS = cx('border-btnSecondaryBorder disabled:border-btnSecondaryBorderDisabled', {
    'active:border-btnSecondaryBorderActive hover:border-btnSecondaryBorderHover': showHoverActive,
  })

  const BUTTON_TEXT_CLASS = cx(
    'text-text-action disabled:text-btnSecondaryTextDisabled bg-transparent border-transparent',
    {
      'active:text-text-action hover:text-text-action hover:border-b hover:border-action': showHoverActive,
    },
  )

  const BUTTON_PRIMARY_LOADING = cx({ [styles['button-primary-loading-bg']]: isLoading && !isDisabled })
  const BUTTON_SECONDARY_LOADING = cx({ [styles['button-secondary-loading-bg']]: isLoading && !isDisabled })
  const BUTTON_DANGER_LOADING = cx({ [styles['button-destruction-loading-bg']]: isLoading && !isDisabled })
  const BUTTON_TEXT_LOADING = cx({ [styles['button-text-loading-bg']]: isLoading && !isDisabled })

  const BUTTON_CURSOR_BASE_STYLE = cx({
    'cursor-pointer': !isDisabled && !isLoading,
    'cursor-not-allowed touch-none': Boolean(isDisabled || isLoading),
  })

  const PRIMARY_STYLES = `${BUTTON_PRIMARY_TEXT_CLASS} ${BUTTON_PRIMARY_BG_CLASS} ${BUTTON_PRIMARY_LOADING}`
  const SECONDARY_STYLES = `${BUTTON_SECONDARY_TEXT_CLASS} ${BUTTON_SECONDARY_BG_CLASS} ${BUTTON_SECONDARY_BORDER_CLASS} ${BUTTON_SECONDARY_LOADING}`
  const DANGER_STYLES = `${BUTTON_PRIMARY_TEXT_CLASS} ${BUTTON_DANGER_BG_CLASS} ${BUTTON_DANGER_LOADING}`
  const TEXT_STYLES = `${BUTTON_TEXT_CLASS} ${BUTTON_TEXT_LOADING}`
  const TEXT_PADDING_STYLES = cx('px-0', {
    'py-2xs': size === 'small',
    'py-xs': size === 'default',
  })

  // Component base class names. Can be overriden by className property.
  const baseClass = cx(
    BUTTON_STYLED_BASE_CLASS,
    BUTTON_STYLED_PADDING_CLASS_MAP[size],
    BUTTON_STYLED_TEXT_SIZE_CLASS_MAP[size],
    BUTTON_CURSOR_BASE_STYLE,

    {
      [PRIMARY_STYLES]: variant === ButtonVariant.Primary,
      [SECONDARY_STYLES]: variant === ButtonVariant.Secondary,
      [DANGER_STYLES]: variant === ButtonVariant.Danger,
      [TEXT_STYLES]: variant === ButtonVariant.Text,
      [TEXT_PADDING_STYLES]: variant === ButtonVariant.Text,
    },
  )

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={type}
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      onClick={(e) => {
        if (!isLoading) {
          onClick && onClick(e)
        }
      }}
      disabled={isDisabled}
    >
      {isLoading && loadingContent ? loadingContent : children}
    </button>
  )
}
